import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { TICKET_API_URL } from "utils/constants";
import { handleApiError } from "utils/errorHandler";
import { request } from "utils/request";
import toastService from "utils/toast";
import { actions } from "../../redux/slice";
import { SprintStatus, updateFiledType } from "../../types";
import { ICompleteSprintBody, ISprintDetails } from "../types";
import { sprintDetailsActions } from "./slice";
import { selectSprintsDetails } from "./selector";

export function* fetchUnresolvedTicketListRequest(action: { payload: string }) {
  try {
    yield delay(500);
    const sprintId = action.payload;

    const requestURL = `${TICKET_API_URL}sprint/unresolved-tickets/${sprintId}`;
    const options = {
      method: "GET",
    };

    yield put(sprintDetailsActions.setTicketListLoading(true));
    const { data, err } = yield call(request, requestURL, options);
    yield put(sprintDetailsActions.setTicketListLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }

    const results = data.data;
    yield put(sprintDetailsActions.setUnresolvedTicket(results));
  } catch (err: any) {
    yield put(sprintDetailsActions.setTicketListLoading(false));
    handleApiError(err);
  }
}
export function* fetchSprintDetailsRequest(action: { payload: string }) {
  try {
    yield delay(500);
    const sprintId = action.payload;

    const requestURL = `${TICKET_API_URL}sprint/details/${sprintId}`;
    const options = {
      method: "GET",
    };

    yield put(sprintDetailsActions.setLoading(true));
    const { data, err } = yield call(request, requestURL, options);
    yield put(sprintDetailsActions.setLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    const results = data.data;
    yield put(sprintDetailsActions.setSprintDetails(results));
  } catch (err: any) {
    yield put(sprintDetailsActions.setLoading(false));
    handleApiError(err);
  }
}
export function* updateSprintRequest(action: {
  payload: { fieldUpdate: updateFiledType; id: string; callback: () => void };
}) {
  yield delay(500);
  try {
    const form: ISprintDetails = yield select(selectSprintsDetails);
    const { id, fieldUpdate } = action.payload;

    let body: Record<string, any> = {};

    // Build changed fields
    if (fieldUpdate === "name") {
      body.name = form.name;
    }
    if (fieldUpdate === "description") {
      body.description = form.description;
    }
    if (fieldUpdate === "scrumMasterId") {
      body.scrumMasterId = form.scrumMaster;
    }
    if (fieldUpdate === "date") {
      body.startDate = form.startDate;
    }
    if (fieldUpdate === "date") {
      body.endDate = form.endDate;
    }
    if (fieldUpdate === "sendRetrospectiveRequest") {
      body.sendRetrospectiveRequest = form.sendRetrospectiveRequest;
    }

    yield put(sprintDetailsActions.setButtonLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/update/${id}`;
    const options = {
      method: "PUT",
      body: JSON.stringify(body),
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(sprintDetailsActions.setButtonLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    yield put(
      actions.updateSprintSuccess({
        id,
        changes: {
          name: form.name,
          description: form.description,
          updatedAt: new Date().toISOString(),
          sendRetrospectiveRequest: form.sendRetrospectiveRequest,
          scrumMasterId: form.scrumMaster,
        },
      }),
    );
    toastService.success(data.message);
    yield call(action.payload.callback);
  } catch (err: any) {
    yield put(sprintDetailsActions.setButtonLoading(false));
    handleApiError(err);
  }
}
export function* launchSprintRequest(action: {
  payload: { sprintId: string; callback: () => void };
}) {
  try {
    yield delay(500);
    const { sprintId } = action.payload;

    yield put(sprintDetailsActions.setButtonLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/launch/${sprintId}`;
    const options = {
      method: "PUT",
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(sprintDetailsActions.setButtonLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    toastService.success(data.message);
    yield put(
      actions.updateSprintSuccess({
        id: sprintId,
        changes: {
          status: SprintStatus.ACTIVE,
        },
      }),
    );
    yield call(action.payload.callback);
  } catch (err: any) {
    yield put(sprintDetailsActions.setButtonLoading(false));
    handleApiError(err);
  }
}
export function* completeSprintRequest(action: {
  payload: {
    sprintId: string;
    body: ICompleteSprintBody;
    callback: () => void;
  };
}) {
  try {
    yield delay(500);
    const { sprintId, body } = action.payload;
    const formData = {
      targetType: body?.targetType,
      targetId: body?.targetId,
      newSprintName: body?.newSprintName,
    };
    yield put(sprintDetailsActions.setButtonLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/complete/${sprintId}`;
    const options = {
      method: "PUT",
      body: JSON.stringify(formData),
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(sprintDetailsActions.setButtonLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    toastService.success(data.message);
    yield put(
      actions.updateSprintSuccess({
        id: sprintId,
        changes: {
          status: SprintStatus.COMPLETED,
        },
      }),
    );
    yield call(action.payload.callback);
  } catch (err: any) {
    yield put(sprintDetailsActions.setButtonLoading(false));
    handleApiError(err);
  }
}
export function* deleteSprintRequest(action: {
  payload: {
    sprintId: string;
    body?: ICompleteSprintBody;
    callback: () => void;
  };
}) {
  try {
    yield delay(500);
    const { sprintId, body } = action.payload;
    const formData = {
      targetType: body?.targetType,
      targetId: body?.targetId,
      newSprintName: body?.newSprintName,
    };
    yield put(sprintDetailsActions.setButtonLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/delete/${sprintId}`;
    const options = {
      method: "DELETE",
      body: JSON.stringify(formData),
    };
    const options1 = {
      method: "DELETE",
    };
    const { data, err } = yield call(
      request,
      requestURL,
      body?.targetId ? options : options1,
    );
    yield put(sprintDetailsActions.setButtonLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    toastService.success(data.message);
    yield put(actions.updateSprintDeleteSuccess(sprintId));

    yield call(action.payload.callback);
  } catch (err: any) {
    yield put(sprintDetailsActions.setButtonLoading(false));
    handleApiError(err);
  }
}
export function* fetchDashboardDetailsRequest(action: { payload: string }) {
  try {
    // Only add delay for first page to improve UX for infinite scroll
    yield delay(500);

    const sprintId = action.payload;

    const requestURL = `${TICKET_API_URL}sprint/dashboard/overview/${sprintId}`;
    const options = {
      method: "GET",
    };

    yield put(sprintDetailsActions.setDashboardLoading(true));

    const { data, err } = yield call(request, requestURL, options);
    yield put(sprintDetailsActions.setDashboardLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    const rData = data.data;
    yield put(sprintDetailsActions.setDashboardDetails(rData));
  } catch (err: any) {
    yield put(sprintDetailsActions.setDashboardLoading(false));
    handleApiError(err);
  }
}

export default function* SprintDetailsSaga() {
  yield takeLatest(
    sprintDetailsActions.fetchSprintDetails,
    fetchSprintDetailsRequest,
  );
  yield takeLatest(sprintDetailsActions.updateSprint, updateSprintRequest);
  yield takeLatest(
    sprintDetailsActions.fetchUnresolvedTicketList,
    fetchUnresolvedTicketListRequest,
  );
  yield takeLatest(sprintDetailsActions.launchSprint, launchSprintRequest);
  yield takeLatest(sprintDetailsActions.completeSprint, completeSprintRequest);
  yield takeLatest(sprintDetailsActions.deleteSprint, deleteSprintRequest);
  yield takeLatest(
    sprintDetailsActions.fetchDashboardDetails,
    fetchDashboardDetailsRequest,
  );
}
